import type {ApiErrorBody, ErrorCode} from './types';
import type {MessageKey} from '../i18n/keys';

/**
 * Every failure this client can produce, in one type.
 *
 * `kind` separates the three things a screen must react to differently:
 *
 *   `api`      the server answered with the contract's error body. `code` is
 *              authoritative and `retriable` is the server's decision.
 *   `network`  the request never got an answer. Always retriable — this is the
 *              4G-in-a-basement case, and it is normal, not exceptional.
 *   `parse`    an answer arrived that this client could not read. Never
 *              retriable; retrying a poison response just burns battery.
 */
export type ApiFailureKind = 'api' | 'network' | 'parse';

export class ApiError extends Error {
  readonly kind: ApiFailureKind;
  readonly status: number | null;
  readonly code: ErrorCode | null;
  readonly requestId: string | null;
  readonly retriable: boolean;
  readonly details: Record<string, unknown> | null;

  constructor(init: {
    kind: ApiFailureKind;
    message: string;
    status?: number | null;
    code?: ErrorCode | null;
    requestId?: string | null;
    retriable?: boolean;
    details?: Record<string, unknown> | null;
    cause?: unknown;
  }) {
    super(init.message, {cause: init.cause});
    this.name = 'ApiError';
    this.kind = init.kind;
    this.status = init.status ?? null;
    this.code = init.code ?? null;
    this.requestId = init.requestId ?? null;
    this.retriable = init.retriable ?? init.kind === 'network';
    this.details = init.details ?? null;
  }

  static fromBody(status: number, body: ApiErrorBody): ApiError {
    return new ApiError({
      kind: 'api',
      message: body.error.message,
      status,
      code: body.error.code,
      requestId: body.error.request_id,
      retriable: body.error.retriable,
      details: body.error.details ?? null,
    });
  }

  static network(cause: unknown): ApiError {
    return new ApiError({
      kind: 'network',
      message: 'Network request failed',
      retriable: true,
      cause,
    });
  }

  /** True when the guest's credential is the problem and re-scanning is the fix. */
  get isSessionProblem(): boolean {
    return (
      this.code === 'unauthenticated' ||
      this.code === 'invalid_token' ||
      this.code === 'token_expired' ||
      this.code === 'tenant_mismatch' ||
      this.code === 'forbidden'
    );
  }

  /** `rate_limited` carries `retry_after_seconds` in `details`. */
  get retryAfterSeconds(): number | null {
    const v = this.details?.['retry_after_seconds'];
    return typeof v === 'number' ? v : null;
  }

  /**
   * The bill already on this table, when a submit was refused because one was.
   *
   * `409 conflict` from `POST /v1/orders` carries `details.order_id` — the
   * contract names it as the ticket a caller must open **instead** of the one it
   * tried to create: "a guest phone scanning an occupied table must open that
   * ticket rather than a new one". Its presence is what distinguishes this
   * conflict from the six other codes that share `error.conflict`.
   */
  get conflictingOrderId(): string | null {
    const v = this.details?.['order_id'];
    return typeof v === 'string' ? v : null;
  }

  /** `item_unavailable` carries `item_ids` in `details`. */
  get unavailableItemIds(): string[] {
    const v = this.details?.['item_ids'];
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  }
}

/**
 * The i18n key to show a guest for a failure.
 *
 * The contract is explicit that `error.message` is unstable English for
 * operators and **must never be shown raw to a Thai-speaking guest**. Clients
 * localise from `code`, which is what this does. Codes that cannot reach a guest
 * surface (staff PINs, device revocation) still map, to `error.generic`, because
 * a surprising code must produce a sentence rather than a blank.
 */
export function errorMessageKey(error: unknown): MessageKey {
  if (!(error instanceof ApiError)) return 'error.generic';
  if (error.kind === 'network') return 'error.network';
  if (error.kind === 'parse') return 'error.generic';

  /*
   * One conflict out of seven, told apart by what it carries.
   *
   * `error.conflict` says "the items changed, look at the latest list again",
   * and it serves `conflict`, `idempotency_key_reuse`, `order_locked`,
   * `unsupported_state_transition`, `precondition_failed`,
   * `precondition_required` and `sequence_out_of_order`. For six of those it is
   * a reasonable thing to say to a guest.
   *
   * For the seventh it is wrong in the way that costs a guest their time. A
   * table that already has a live bill refuses a second one, and the API says so
   * with `details.order_id`. Telling that guest the menu changed sends them back
   * to re-read a menu with nothing wrong with it, looking for a difference that
   * is not there, while the actual answer — their food is already ordered — is
   * one screen away. Seen on staging 2026-08-14 with a real scan.
   */
  if (error instanceof ApiError && error.conflictingOrderId) {
    return 'error.tableHasOpenBill';
  }

  switch (error.code) {
    case 'unauthenticated':
    case 'invalid_token':
    case 'token_expired':
    case 'tenant_mismatch':
      return 'error.session';
    case 'forbidden':
      return 'error.forbidden';
    case 'not_found':
      return 'error.notFound';
    case 'item_unavailable':
      return 'error.itemUnavailable';
    case 'rate_limited':
      return 'error.rateLimited';
    case 'validation_failed':
      return 'error.validation';
    case 'conflict':
    case 'idempotency_key_reuse':
    case 'order_locked':
    case 'unsupported_state_transition':
    case 'precondition_failed':
    case 'precondition_required':
    case 'sequence_out_of_order':
      return 'error.conflict';
    case 'unavailable':
    case 'internal':
    case 'upstream_timeout':
      return 'error.unavailable';
    case 'not_implemented':
      return 'error.notAvailableHere';
    default:
      return 'error.generic';
  }
}
